import {Component, effect, ElementRef, inject, input, output, signal, viewChild} from "@angular/core";
import {
  CapacitorBarcodeScanner,
  CapacitorBarcodeScannerAndroidScanningLibrary,
  CapacitorBarcodeScannerCameraDirection,
  CapacitorBarcodeScannerScanOrientation,
  CapacitorBarcodeScannerTypeHintALLOption
} from "@capacitor/barcode-scanner";
import {environment} from "src/app/environments/environment";
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {NgOptimizedImage} from "@angular/common";
import {
  InternalNotificationsStore,
  InternalNotificationType
} from "src/app/infrastructure/internal-notifications/internal-notifications.store";

const cancellationErrorCode = 'OS-PLUG-BARC-0006';
const noCameraPermissionCode = 'OS-PLUG-BARC-0007';

export interface DeviceIdSelected {
  id: string,
  manualInput: boolean
}

const inputElement = (node: ChildNode | null) => {
  if (!node) return;

  if (node instanceof HTMLInputElement) return node as HTMLInputElement;

  return;
}

const trim = (value?: string) => {
  if (!value) return value;
  return value.trim();
}

@Component({
  selector: 'app-device-id-input',
  templateUrl: './device-id-input.component.html',
  imports: [GenericButtonComponent, NgOptimizedImage],
  styleUrl: './device-id-input.component.css'
})
export class DeviceIdInputComponent {

  inputWrapper = viewChild.required<ElementRef<HTMLDivElement>>('inputWrapper');

  showImage = input(true);
  deviceName = input.required<string>();
  deviceIdSelected = output<DeviceIdSelected>();
  inputLength = input.required<number>();

  deviceIdParts = signal<string[]>([]);
  indexes = signal<number[]>([]);

  isNativeDevice = !['', 'web'].includes(environment.platform);

  private internalNotificationsStore = inject(InternalNotificationsStore);

  constructor() {
    effect(() => {
      const hardwareIdParts = [];
      const indexes = [];
      for (let i = 0; i < this.inputLength(); i++) {
        hardwareIdParts.push('');
        indexes.push(i);
      }
      this.deviceIdParts.set(hardwareIdParts);
      this.indexes.set(indexes);
    });
  }

  /*
    This workaround was introduced because Angular's (keydown.backspace) binding
    doesn't work in iOS's webview.
  */
  keydown(event: Event) {
    const keyboardEvent = event as KeyboardEvent;

    const currentInput = event.target as HTMLInputElement;
    const previousInput = inputElement(currentInput.previousSibling);

    if (keyboardEvent.key === 'Backspace' && previousInput !== undefined) this.moveFocusBack(currentInput, previousInput);
    if (keyboardEvent.key === 'Enter') this.acceptManualDeviceId();
  }

  acceptManualDeviceId() {
    this.deviceIdSelected.emit({
      id: this.deviceIdParts().join("").toLowerCase(),
      manualInput: true
    });
  }

  addDeviceIdPart(number: number, $event: Event) {
    const inputEvent = $event as InputEvent;

    const currentInput = inputEvent.target as HTMLInputElement;
    const nextInput = inputElement(currentInput.nextSibling);

    this.deviceIdParts()[number] = currentInput.value;
    if (currentInput.value.length !== 1) return;

    if (nextInput) {
      nextInput.focus();
    } else {
      this.acceptManualDeviceId();
    }
  }

  moveFocusBack(currentInput: HTMLInputElement, previousInput?: HTMLInputElement) {
    if (!currentInput.value && previousInput) {
      previousInput.focus();
    }
  }

  public async scanBarcode(): Promise<void> {
    try {
      const barcodeResult = await CapacitorBarcodeScanner.scanBarcode({
        hint: CapacitorBarcodeScannerTypeHintALLOption.ALL,
        android: { scanningLibrary: CapacitorBarcodeScannerAndroidScanningLibrary.MLKIT },
        scanText: "Scanning...",
        scanInstructions: "Please hold the QR code in the center",
        web: {
          showCameraSelection: true,
          scannerFPS: 30,
        },
        cameraDirection: CapacitorBarcodeScannerCameraDirection.BACK,
        scanOrientation: CapacitorBarcodeScannerScanOrientation.PORTRAIT,
      });

      const scanResult = trim(barcodeResult.ScanResult);

      if (!scanResult) {
        this.internalNotificationsStore.addNotification("Error occurred during scan: empty scan result.", InternalNotificationType.ERROR, false);
        return;
      }

      this.deviceIdSelected.emit({
        id: scanResult,
        manualInput: false
      });
    } catch (e: any) {
      if (e.code === cancellationErrorCode) {
        // Ignore cancellation
      } else if (e.code === noCameraPermissionCode) {
        this.internalNotificationsStore.addNotification("Cannot perform scan without permission to access camera.", InternalNotificationType.ERROR, true);
      } else {
        this.internalNotificationsStore.addNotification("An unknown error occurred during scan.", InternalNotificationType.ERROR, false);
      }
    }
  }

  clearInput() {
    this.deviceIdParts.set(this.deviceIdParts().map(_ => ''));

    const firstInput = inputElement(this.inputWrapper().nativeElement.firstChild);
    if (!firstInput) return;

    firstInput.focus();
  }

  manualInputStarted() {
    return this.deviceIdParts().some(digit => digit != '')
  }
  manualInputCompleted() {
    return this.deviceIdParts().length === this.inputLength() && this.deviceIdParts().every(digit => digit != '')
  }
}
